import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import type { FaxErrorInboxItem } from '@seen/core/models/inbox'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isOfType } from '@seen/core/utils'
import { TypographyDetail } from '@seen/ui'
import { IconAlertCircle } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { SidebarHeader } from '#src/pages/inbox/detail/SidebarHeader.tsx'

/* Fax error viewer — surfaces sender, recipient, status reason, and a link
   to the participant timeline where the failed fax row is highlighted. */
export function FaxErrorDetailView({
  item,
  notificationItem,
  onClose,
}: {
  item: FaxErrorInboxItem
  notificationItem: NotificationItem
  onClose: () => void
}): ReactNode {
  const { data: communication } = useRead(
    ExtendedCommunication,
    item.communication
  )
  const recipientPractitionerRef = isOfType(item.recipient, 'Practitioner')
    ? item.recipient
    : undefined
  const recipientOrgRef = isOfType(item.recipient, 'Organization')
    ? item.recipient
    : undefined
  const { data: recipientPractitioner } = useRead(
    ExtendedPractitioner,
    recipientPractitionerRef
  )
  const { data: recipientOrg } = useRead(ExtendedOrganization, recipientOrgRef)
  const recipientName = recipientPractitioner?.name ?? recipientOrg?.name

  const title = recipientName
    ? `Fax failed to send to ${recipientName}`
    : 'Fax failed to send'

  return (
    <div className="flex h-full flex-col border-l border-border bg-background">
      <SidebarHeader
        title={title}
        subtitle={item.statusReason}
        actionUrl={item.actionUrl}
        actionLabel="Open in timeline"
        notificationItem={notificationItem}
        onClose={onClose}
      />
      <ScrollArea
        className="flex-1"
        scrollRestorationId="inbox-detail-fax-error"
      >
        <div className="flex flex-col gap-3 p-4">
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3">
            <IconAlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
            <TypographyDetail>{item.statusReason}</TypographyDetail>
          </div>
          {communication?.message && (
            <div className="flex flex-col gap-1">
              <TypographyDetail className="text-foreground-secondary">
                Message
              </TypographyDetail>
              <TypographyDetail>{communication.message}</TypographyDetail>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
